import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoginButton from './LoginButton';
import LogoutButton from './LogoutButton';
import TokensPage from '../pages/TokensPage';
import PhotoslopCreate from '../pages/PhotoslopCreate';
import DeckDashboard from '../pages/DeckDashboard';
import DeckEditorPage from '../pages/DeckEditorPage';
import PublicFeed from './PublicFeed';
import RetroWordmark from './RetroWordmark';
import Home from './Home';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCoins, FaCaretDown, FaGlobe, FaHeart, FaPencilAlt, FaChartBar } from 'react-icons/fa';
import { fetchTokenBalance, fetchTokenAllowance } from '../utils/tokenUtils';
import ImageCarousel from './ImageCarousel';
import ConfirmDialog from './ConfirmDialog';
import NumberSelectionDialog from './NumberSelectionDialog';
import Drawings from '../pages/Drawings';
import AdminDashboard from '../pages/AdminDashboard';
import Support from '../pages/Support';
import { FOUNDER_EMAILS } from '../config';
import Navbar from './Navbar';
import AdminSubNav from './AdminSubNav';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Configure axios defaults
axios.defaults.baseURL = API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true;  // Enable credentials for CORS

// Add request interceptor for auth tokens and URL protocol handling
axios.interceptors.request.use(async (config) => {
  try {
    // Force HTTP for localhost URLs
    if (config.url?.includes('localhost')) {
      config.url = config.url.replace('https://', 'http://');
    }
    if (config.baseURL?.includes('localhost')) {
      config.baseURL = config.baseURL.replace('https://', 'http://');
    }

    const auth0 = window.auth0;
    if (auth0) {
      const token = await auth0.getTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email read:images write:images"
        }
      });
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
          // Auth token error - silenced for production
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor for error handling
axios.interceptors.response.use((response) => {
  return response;
}, (error) => {
  console.error('[RETRO] 🌐 API error:', {
    status: error.response?.status,
    data: error.response?.data,
    config: {
      url: error.config?.url,
      method: error.config?.method,
      headers: {
        ...error.config?.headers,
        Authorization: error.config?.headers?.Authorization ? 'Bearer [REDACTED]' : undefined
      }
    },
    timestamp: new Date().toISOString()
  });
  return Promise.reject(error);
});

const GridComponentWrapper = ({ gridProps, GridComponent }) => {
  console.log('[RETRO-ROUTING] 🛣️ Catch-all route matched:', {
    pathname: window.location.pathname,
    GridComponent: GridComponent?.name,
    timestamp: new Date().toISOString()
  });
  
  return <GridComponent {...gridProps} />;
};

const AppContent = ({ children, GridComponent, isFounderOnly = false, isPublicRoute = false }) => {
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    getAccessTokenSilently,
    loginWithRedirect
  } = useAuth0();
  const [showDropdown, setShowDropdown] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [monthlyAllowance, setMonthlyAllowance] = useState(0);
  const [tokenFont, setTokenFont] = useState('VT323');
  const location = useLocation();
  const navigate = useNavigate();
  const wordmarkRef = useRef();
  const [images, setImages] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newImageIds, setNewImageIds] = useState(new Set());
  const [generationProgress, setGenerationProgress] = useState({});
  const [resetRotationKey, setResetRotationKey] = useState(0);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [isHighlightingLikes, setIsHighlightingLikes] = useState(false);
  const [sortOrder, setSortOrder] = useState('newest');
  const [showNumberDialog, setShowNumberDialog] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [isHighlightingPublic, setIsHighlightingPublic] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [publicCount, setPublicCount] = useState(0);
  const [showPublicOnly, setShowPublicOnly] = useState(false);
  const [hasDrawings, setHasDrawings] = useState(false);
  const [isCompactGrid, setIsCompactGrid] = useState(false);
  const [isGroupedByPrompt, setIsGroupedByPrompt] = useState(false);

  // Check founder status
  const isFounder = useMemo(() => {
    const hasFounderAccess = user?.email && FOUNDER_EMAILS.includes(user.email);
    return hasFounderAccess;
  }, [user?.email]);

  // Check if current route is an admin route
  const isAdminRoute = useMemo(() => {
    return ['/bs', '/logo-creator', '/retro-arcade'].includes(location.pathname);
  }, [location.pathname]);

  // Handle founder-only routes
  useEffect(() => {
    if (!authLoading && isFounderOnly) {
      if (!isAuthenticated) {
        navigate('/create');
        return;
      }
      if (!isFounder) {
        navigate('/create');
        return;
      }
    }
  }, [authLoading, isAuthenticated, isFounder, isFounderOnly, navigate, location.pathname, user?.email]);

  const getAuthHeaders = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email read:images write:images read:favorites write:favorites"
        }
      });
      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const updateTokenInfo = async () => {
      try {
        const [balance, allowance] = await Promise.all([
          fetchTokenBalance(getAccessTokenSilently),
          fetchTokenAllowance(getAccessTokenSilently)
        ]);
        setTokenBalance(balance);
        setMonthlyAllowance(allowance);
      } catch (error) {
        console.error('Failed to fetch token info:', error);
      }
    };

    if (isAuthenticated && !authLoading) {
      updateTokenInfo();
    }
  }, [isAuthenticated, authLoading, getAccessTokenSilently]);

  // Check for successful purchase
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      // Add a delay to ensure webhook has processed
      setTimeout(async () => {
        try {
          const [balance, allowance] = await Promise.all([
            fetchTokenBalance(getAccessTokenSilently),
            fetchTokenAllowance(getAccessTokenSilently)
          ]);
          setTokenBalance(balance);
          setMonthlyAllowance(allowance);
        } catch (error) {
          console.error('Failed to update token info after purchase:', error);
        }
      }, 2000);
    }
  }, [location.search, getAccessTokenSilently]);

  // Check for drawings on mount and auth state change
  useEffect(() => {
    const checkForDrawings = async () => {
      if (!isAuthenticated) {
        setHasDrawings(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/drawings`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const drawings = await response.json();
          setHasDrawings(drawings.length > 0);
          localStorage.setItem('hasDrawings', drawings.length > 0 ? 'true' : 'false');
        }
      } catch (error) {
        console.error('Failed to check for drawings:', error);
      }
    };

    checkForDrawings();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Also check localStorage for hasDrawings updates
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'hasDrawings') {
        setHasDrawings(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // Check initial value
    const savedHasDrawings = localStorage.getItem('hasDrawings') === 'true';
    setHasDrawings(savedHasDrawings);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const triggerWordmarkAnimation = () => {
    wordmarkRef.current?.click();
  };

  const handleWordmarkClick = () => {
    triggerWordmarkAnimation();
  };

  const handleNumberSelection = (number) => {
    setShowNumberDialog(false);
    // Add your number selection logic here
  };

  const handleConfirmDelete = () => {
    if (imageToDelete) {
      handleDelete(imageToDelete);
      setImageToDelete(null);
    }
    setShowDeleteDialog(false);
  };

  const handleDelete = async (imageId) => {
    // Implement delete logic
  };

  const handleToggleFavorite = async (imageId) => {
    // Implement toggle favorite logic
  };

  const handleRegenerate = async (prompt, numImages, sourceImage) => {
    // Implement regenerate logic
  };

  const handleCopyToPrompt = (prompt) => {
    setSelectedPrompt(prompt);
  };

  const handleImageLoaded = (imageId) => {
    // Implement image loaded logic
  };

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setPage(prev => prev + 1);
  };

  // Create a shared props object for grid components
  const gridProps = {
    // Filter props
    showFavoritesOnly,
    setShowFavoritesOnly,
    showPublicOnly,
    setShowPublicOnly,
    isHighlightingLikes,
    setIsHighlightingLikes,
    isHighlightingPublic,
    setIsHighlightingPublic,
    // Sort props
    sortOrder,
    setSortOrder,
    // Group props
    isGroupedByPrompt,
    setIsGroupedByPrompt,
    showGrouping: true,
    // View props
    onFullscreenClick: () => {},
    showFullscreen: false,
    isCompactGrid,
    setIsCompactGrid,
    // Auth & counts
    isAuthenticated,
    favoritesCount,
    publicCount,
    // Optional features
    showPublicToggle: true,
    showCompactToggle: true,
    showPopularSort: true,
    showLikesControls: true,
    // Data props
    images,
    favorites,
    onToggleFavorite: handleToggleFavorite,
    onDelete: handleDelete,
    newImageIds,
    generationProgress,
    onImageClick: (image) => setSelectedImage(image),
    onFavorite: triggerWordmarkAnimation,
    onImageLoaded: handleImageLoaded,
    onRegenerate: handleRegenerate,
    resetRotationKey,
    onCopyToPrompt: handleCopyToPrompt,
    // Loading props
    hasMore,
    loadingMore,
    onLoadMore: handleLoadMore,
    // User props
    userId: user?.sub,
    user,
    // Create page specific props
    isGenerating: isLoading,
    handleSubmit: handleRegenerate,
    defaultPrompt: selectedPrompt,
    setDefaultPrompt: setSelectedPrompt,
    handleTogglePublic: handleToggleFavorite
  };

  const renderTokensPage = useCallback(() => {
    return (
      <TokensPage 
        tokenBalance={tokenBalance} 
        onTokenBalanceChange={setTokenBalance}
        monthlyAllowance={monthlyAllowance}
        isFounder={isFounder}
        monthlyUsage={0} // TokensPage now calculates its own usage
      />
    );
  }, [isFounder, tokenBalance, monthlyAllowance, user?.email]);

  // Wait for Auth0 to fully initialize before making authentication decisions
  // This prevents the redirect to Home during page reloads while Auth0 restores the session
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7F4]">
        <div className="w-6 h-6 rounded-full border-2 border-[#00FF9F]/30 border-t-[#00FF9F] animate-spin"></div>
      </div>
    );
  }

  // Only redirect to Home if we're certain the user is not authenticated
  // and we're not on a founder-only route or public route
  if (!isAuthenticated && !isFounderOnly && !isPublicRoute) {
    return <Home />;
  }

  // If founder-only and not a founder, redirect is handled by useEffect
  if (isFounderOnly && !isFounder) {
    return null;
  }

  // Redirect to /create after successful authentication
  if (isAuthenticated && location.pathname === '/') {
    return <Navigate to="/create" replace />;
  }

  // Hide navbar when in InvisibleLooper view
  const shouldHideNavbar = location.pathname.includes('/admin/invisible-looper') || 
                           location.pathname.includes('/admin/message-screengrab');

  return (
    <div className="min-h-screen bg-[#F4F3F0]">
      {!shouldHideNavbar && <Navbar hasDrawings={hasDrawings} />}
      {/* Admin SubNav - only show on specific admin sub-pages */}
      {location.pathname.includes('/admin/') && 
       (location.pathname.includes('/bs') || 
        location.pathname.includes('/logo-creator') || 
        location.pathname.includes('/retro-arcade')) && (
        <AdminSubNav />
      )}
      <main
        className={
          shouldHideNavbar ||
          location.pathname === '/create' ||
          location.pathname.startsWith('/decks')
            ? 'px-0 py-0'
            : 'container mx-auto px-4 py-8'
        }
      >
        {children || (
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/tokens" element={renderTokensPage()} />
            <Route path="/create" element={
              <PhotoslopCreate
                tokenBalance={tokenBalance}
                onTokenBalanceChange={setTokenBalance}
                monthlyAllowance={monthlyAllowance}
              />
            } />
            <Route path="/decks" element={<DeckDashboard />} />
            <Route path="/decks/:deckId/edit" element={<DeckEditorPage />} />
            <Route path="/public" element={
              <div className="max-w-[920px] mx-auto">
                <PublicFeed onFavorite={triggerWordmarkAnimation} />
              </div>
            } />
            <Route path="/drawings" element={<Drawings />} />
            <Route path="/support" element={<Support />} />
            <Route path="/admin/*" element={<AdminDashboard />} />
            <Route path="/*" element={
              GridComponent ? <GridComponentWrapper gridProps={gridProps} GridComponent={GridComponent} /> : null
            } />
          </Routes>
        )}
      </main>
    </div>
  );
};

export default AppContent; 